import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Shield, Clock, ChevronRight, ChevronDown, Send, Loader2, Image, Bot, Layers, Rocket, Zap, TrendingUp, User, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { devWarn } from "@/lib/uiStorage";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { StickyCTABar } from "@/components/layout/StickyCTABar";
import { LiveProofTicker } from "@/components/layout/LiveProofTicker";
import { PasteLinkVisual, ScriptVisual, RenderVisual, ScoreVisual } from "@/components/layout/StepVisuals";
import { HeroDemoVideo } from "@/components/layout/HeroDemoVideo";
import { AdExamplesGallery } from "@/components/layout/AdExamplesGallery";
import { LocalSocialProofStrip } from "@/components/layout/LocalSocialProofStrip";
import { useCurrency } from "@/hooks/useCurrency";
import { SEO } from "@/components/seo/SEO";
import { AnimatedSection, fadeInUp } from "@/components/layout/AnimatedSection";
import { PACKAGES, priceFor, formatMoney } from "@/config/pricing";
import { PricingSection } from "@/components/landing/PricingSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HeroSection = () => {
  const { user } = useAuth();
  const { currency } = useCurrency();
  const fromPrice = formatMoney(priceFor(PACKAGES.find((p) => p.id === "starter"), currency), currency);
  return (
    <section className="relative flex items-center pt-16 md:pt-20 overflow-hidden" data-testid="hero-section">
      <div className="orb-1 -top-20 -left-40 animate-pulse-glow" />
      <div className="orb-2 top-1/2 right-0 animate-pulse-glow" style={{ animationDelay: '2s' }} />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-14">
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="headline-poppins leading-[1.02] mb-8 md:mb-10 text-balance" data-testid="hero-headline">
          <span className="xl:whitespace-nowrap"><span className="gradient-text-warm">Launch 50 ad variations</span> <span className="text-white">before</span></span><br className="hidden xl:block" />{" "}
          <span className="text-white xl:whitespace-nowrap">your competitor launches one.</span>
        </motion.h1>
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="min-w-0">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-violet-300 mb-6">
              <Sparkles className="w-4 h-4" /> Built for AU &amp; NZ Shopify brands · 3–5 fresh variants a week · Beat creative fatigue
            </span>
            <p className="text-lg text-white/80 mb-6 max-w-lg leading-relaxed" data-testid="hero-subtitle">
              Lumora generates, scores, and ships performance-focused ads for Shopify, TikTok, Meta and Google.
              <span className="block mt-2">Find winners faster, with creative that converts.</span>
              <span className="block mt-2 text-white/90">No filming. No creators. No briefs. Plans from <span className="text-violet-300 font-bold" data-testid="hero-from-price">{fromPrice}/mo</span>.</span>
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild className="rounded-full px-8 py-6 text-lg bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white font-bold shadow-lg shadow-violet-500/25 hover:shadow-violet-500/50 transform hover:-translate-y-1 transition-all btn-shine" data-testid="hero-get-started-btn">
                {user ? (
                  <Link to="/studio">Open Studio <Wand2 className="ml-2 w-5 h-5" /></Link>
                ) : (
                  <Link to="/register">
                    Start Free — No Credit Card <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                )}
              </Button>
              <Button asChild variant="outline" className="rounded-full px-8 py-6 text-lg font-bold text-white border-white/20 hover:bg-white/5 backdrop-blur-sm" data-testid="hero-learn-more-btn">
                <a href="#examples">See Examples</a>
              </Button>
            </div>

            {/* URL-to-Ad inline demo input — surfaces the killer feature */}
            <div className="mt-8 glass rounded-2xl p-5 border border-violet-500/20" data-testid="hero-url-to-ad">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-violet-300" />
                <span className="text-violet-300 text-xs font-bold uppercase tracking-wider">Paste a URL · Get an ad in 2 min</span>
              </div>
              <p className="text-white/60 text-sm mb-3">Drop in any Shopify, Amazon or product page — we&apos;ll auto-prompt the ad for you.</p>
              <div className="flex gap-2">
                <input
                  type="url"
                  placeholder="https://yourstore.com/products/your-product"
                  className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 text-sm focus:border-violet-400/50 outline-none"
                  data-testid="hero-url-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { window.location.href = user ? "/studio?url=" + encodeURIComponent(e.target.value) : "/register"; } }}
                />
                <Button asChild className="rounded-xl px-5 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white font-bold shrink-0" data-testid="hero-url-cta">
                  <Link to={user ? "/studio" : "/register"}>Generate <ArrowRight className="ml-1 w-4 h-4" /></Link>
                </Button>
              </div>
            </div>

            <div className="mt-6" />
            <LiveProofTicker />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative hidden md:block md:mt-2">
            {/* Sparkle particles */}
            <div className="absolute -inset-12 z-[15] pointer-events-none overflow-visible">
              <div className="sparkle-particle sp-1" />
              <div className="sparkle-particle sp-2" />
              <div className="sparkle-particle sp-3" />
              <div className="sparkle-particle sp-4" />
            </div>

            {/* Layered glow backdrop */}
            <div className="absolute -inset-12 bg-purple-600/15 blur-[100px] rounded-full -z-10" />
            <div className="absolute -inset-6 bg-indigo-500/10 blur-[60px] rounded-full -z-10 animate-pulse" style={{ animationDuration: '4s' }} />

            {/* 15s looping screen capture of a real Studio session (falls back to the montage). */}
            <HeroDemoVideo />

            {/* Floating card: AI Status */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }} className="absolute -left-6 -top-5 z-20">
              <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl backdrop-blur-xl bg-white/[0.07] border border-white/10 shadow-lg shadow-black/20">
                <div className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
                <span className="text-xs font-medium text-white/80 mono">Live workflow</span>
              </div>
            </motion.div>

            {/* Floating card: Automation badge */}
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.2 }} className="absolute -bottom-4 left-8 z-20">
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl backdrop-blur-xl bg-white/[0.07] border border-white/10 shadow-lg shadow-black/20">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                  <Rocket className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-white/70">Powered by xAI Grok</p>
                  <p className="text-sm font-bold text-violet-400">Image + Video</p>
                </div>
              </div>
            </motion.div>

            {/* Decorative ring */}
            <div className="absolute -top-3 -right-3 w-24 h-24 rounded-full border border-purple-500/20 -z-[5]" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full border border-indigo-500/10 -z-[5]" />
          </motion.div>
        </div>
        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="flex justify-center mt-12"
        >
          <a href="#services" className="flex flex-col items-center gap-2 text-white/20 hover:text-white/40 transition-colors group">
            <span className="text-xs tracking-widest uppercase">Scroll</span>
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const OutcomeValuePropsSection = () => {
  const outcomes = [
    { icon: <Zap className="w-5 h-5" />, value: "2 min", label: "URL → finished ad", sub: "paste a product link, get script, scenes & voiceover", gradient: "from-violet-500 to-indigo-600" },
    { icon: <Layers className="w-5 h-5" />, value: "6", label: "Ad formats per product", sub: "hero, lifestyle, unboxing, testimonial, compare, b-roll", gradient: "from-cyan-500 to-blue-600" },
    { icon: <TrendingUp className="w-5 h-5" />, value: "0–100", label: "Creative Score on every ad", sub: "hook, CTA, psychology & conversion — before you spend", gradient: "from-violet-600 to-indigo-600" },
    { icon: <Rocket className="w-5 h-5" />, value: "AU/NZ", label: "Compliance-checked claims", sub: "ACCC, TGA, AANA & NZ ASA rules applied automatically", gradient: "from-fuchsia-500 to-pink-600" },
  ];
  return (
    <AnimatedSection className="py-14 md:py-20 relative" id="outcomes">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-violet-300 mb-4"><TrendingUp className="w-4 h-4" />Outcomes, not units</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3"><span className="gradient-text">Built to find winners, not just make ads.</span></h2>
          <p className="text-white/55 max-w-2xl mx-auto text-sm md:text-base">Every ad Lumora produces is scored and compliance-checked before you spend a dollar, so you test more creative and keep what converts.</p>
        </motion.div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-testid="outcomes-grid">
          {outcomes.map((o) => (
            <motion.div key={o.label} variants={fadeInUp} className="rounded-2xl p-5 md:p-6 glass glass-hover card-hover group">
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${o.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>{o.icon}</div>
              <p className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">{o.value}</p>
              <p className="text-sm font-bold text-white/85 mt-1">{o.label}</p>
              <p className="text-xs text-white/45 mt-1">{o.sub}</p>
            </motion.div>
          ))}
        </div>
        {/* ROI Calculator + Score-your-Ad lead-magnet strip */}
        <motion.div variants={fadeInUp} className="mt-10 grid md:grid-cols-2 gap-4">
          <Link to="/score-your-ad?tab=roi" className="rounded-2xl p-5 md:p-6 glass card-hover flex items-center gap-4 group" data-testid="roi-calc-cta">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm md:text-base">Calculate your potential ROI</p>
              <p className="text-white/50 text-xs md:text-sm">Model what e.g. a 15% CPA reduction would mean for your spend — takes 30 seconds.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-violet-300 group-hover:translate-x-1 transition-all" />
          </Link>
          <Link to="/score-your-ad" className="rounded-2xl p-5 md:p-6 glass card-hover flex items-center gap-4 group" data-testid="score-ad-cta">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm md:text-base">Score your ad — free</p>
              <p className="text-white/50 text-xs md:text-sm">Get an instant AI critique on hook, retention, and conversion likelihood.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-violet-300 group-hover:translate-x-1 transition-all" />
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

const ServicesSection = () => {
  const services = [
    { icon: <Rocket className="w-6 h-6" />, title: "URL → Ad in Seconds", description: "Paste any Shopify, Amazon, or store URL. Lumora scrapes the page, picks an ad format, and hands you a ready-to-generate prompt — your first creative takes 30 seconds.", gradient: "from-cyan-500 to-blue-600", glow: "group-hover:shadow-cyan-500/20", featured: true },
    { icon: <Layers className="w-6 h-6" />, title: "5 Variants, One Click", description: "Found a winner? Spin 5 fresh angles — low-angle, flat-lay, golden-hour, B-roll, lifestyle — without rewriting a single prompt. Built for A/B testing at scale.", gradient: "from-fuchsia-500 to-pink-600", glow: "group-hover:shadow-fuchsia-500/20" },
    { icon: <Image className="w-6 h-6" />, title: "Hyper-Realistic AI Models", description: "Generate photo-real influencer models holding your product — beauty, fashion, fitness, tech, Fanvue. Tasteful UGC or product showcase, your call.", gradient: "from-violet-600 to-indigo-600", glow: "group-hover:shadow-violet-500/20" },
    { icon: <Bot className="w-6 h-6" />, title: "AI Video Ads", description: "Animate any image into a cinematic video, or generate UGC product demos and unboxings from a single prompt — powered by xAI Grok.", gradient: "from-violet-500 to-indigo-600", glow: "group-hover:shadow-violet-500/20" }
  ];
  return (
    <AnimatedSection className="py-20 md:py-24 relative" id="services">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-cyan-300 mb-4"><Rocket className="w-4 h-4" />Built for Speed</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4"><span className="gradient-text">From Product Page to Winning Ad</span></h2>
          <p className="text-white/65 max-w-3xl mx-auto">Skip the script-writing, the actor casting, the studio rental. Paste a URL, pick a format, and let Lumora ship the creative — then auto-spin variants until you hit your winner.</p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="services-grid">
          {services.map((service) => (
            <motion.div key={service.title} variants={fadeInUp} className={`relative rounded-2xl p-7 glass glass-hover card-hover group transition-shadow ${service.glow}`}>
              {service.featured && (
                <span className="absolute -top-2 right-4 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-[10px] font-bold tracking-wider uppercase">New</span>
              )}
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>{service.icon}</div>
              <h3 className="text-lg font-bold mb-3 text-white">{service.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const HowItWorksSection = () => {
  const steps = [
    { number: "01", title: "Paste your product link", description: "Shopify URL in, product photos, price and copy out — or add a product manually.", icon: <User className="w-6 h-6" />, gradient: "from-cyan-500 to-blue-600", Visual: PasteLinkVisual },
    { number: "02", title: "AI writes the ad", description: "Script, hook and captions written for AU/NZ shoppers and checked against ACCC, TGA and platform rules.", icon: <Sparkles className="w-6 h-6" />, gradient: "from-violet-600 to-indigo-600", Visual: ScriptVisual },
    { number: "03", title: "Scenes & voiceover render", description: "Your real product in 4 UGC-style scenes, narrated, cut to a vertical video.", icon: <Wand2 className="w-6 h-6" />, gradient: "from-fuchsia-500 to-purple-600", Visual: RenderVisual },
    { number: "04", title: "Score, pick, ship", description: "Every variant gets a Creative Score — launch the winner to Meta and TikTok.", icon: <TrendingUp className="w-6 h-6" />, gradient: "from-violet-500 to-indigo-600", Visual: ScoreVisual }
  ];
  return (
    <AnimatedSection className="py-20 md:py-24 relative" id="how-it-works">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-cyan-300 mb-4"><Zap className="w-4 h-4" />The Process</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4"><span className="gradient-text">How It Works</span></h2>
        </motion.div>
        {/* Connecting line behind steps (desktop only) */}
        <div className="relative">
          <div className="hidden lg:block absolute top-[68px] left-[12%] right-[12%] h-[2px] bg-gradient-to-r from-cyan-500/30 via-violet-500/40 to-violet-500/30 z-0" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10" data-testid="how-it-works-steps">
            {steps.map((step, index) => (
              <motion.div key={step.number} variants={fadeInUp} className="relative group">
                <div className="rounded-2xl p-6 glass glass-hover h-full card-hover">
                  <step.Visual />
                  <div className="flex items-center gap-3 mt-5 mb-3">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${step.gradient} flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shrink-0`}>{step.icon}</div>
                    <span className="mono text-sm text-purple-300">{step.number}</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-white">{step.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{step.description}</p>
                </div>
                {index < 3 && <div className="hidden lg:block absolute top-[68px] -right-4 transform"><ChevronRight className="w-8 h-8 text-purple-500/40" /></div>}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "", package_interest: "" });
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await axios.post(`${API}/enquiry`, formData); toast.success("Thank you! We'll be in touch soon."); setFormData({ name: "", email: "", phone: "", message: "", package_interest: "" }); }
    catch (error) { toast.error("Failed to submit. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <AnimatedSection className="py-20 md:py-24 relative" id="contact">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          <motion.div variants={fadeInUp}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-purple-300 mb-6"><Send className="w-4 h-4" />Get In Touch</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6"><span className="gradient-text">Ready to Scale?</span></h2>
            <p className="text-white/60 mb-8 max-w-lg">Want to learn how Lumora can transform your e-commerce ad creative? Fill out the form and our team will get back to you within 24 hours.</p>
            <div className="space-y-6">
              <div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center"><Shield className="w-6 h-6 text-purple-400" /></div><div><p className="font-bold text-white">Secure & Confidential</p><p className="text-white/50 text-sm">Your information is protected</p></div></div>
              <div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center"><Clock className="w-6 h-6 text-cyan-400" /></div><div><p className="font-bold text-white">Quick Response</p><p className="text-white/50 text-sm">We reply within 24 hours</p></div></div>
            </div>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <form onSubmit={handleSubmit} className="rounded-3xl p-8 glass" data-testid="contact-form">
              <div className="space-y-6">
                <div><label className="block text-white/70 text-sm mb-2">Full Name *</label><Input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="bg-white/5 border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-purple-500 w-full" /></div>
                <div><label className="block text-white/70 text-sm mb-2">Email Address *</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@example.com" className="bg-white/5 border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-purple-500 w-full" /></div>
                <div><label className="block text-white/70 text-sm mb-2">Message *</label><Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Tell us about your goals..." rows={4} className="bg-white/5 border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-purple-500 w-full resize-none" /></div>
                <Button type="submit" disabled={loading} className="w-full rounded-full py-6 text-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send Message <Send className="ml-2 w-5 h-5" /></>}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

const FAQ_ITEMS = [
  { q: "What counts as one ad?", a: "One ad is a finished 15-second vertical video ad: script, creator, voice and captions, ready to upload to TikTok, Meta or YouTube Shorts. A 30-second ad is two 15-second clips stitched together, so it uses 2 ads. Each ad is 10 credits." },
  { q: "What do images and short clips cost?", a: "Images are 2 credits each. A 5-second B-roll clip is 5 credits. Voiceovers are 1 credit (2 for HD or expressive voices). Premium video quality uses 3x the credits of the standard option." },
  { q: "What is in the free plan?", a: "30 free credits with no credit card. That is enough for 1 watermarked 15-second ad, or around 15 images. Free accounts are limited to one video ad; after that you can keep making images until your credits run out, or move to Starter for 3 finished ads a month." },
  { q: "Do unused credits roll over?", a: "On Free, Starter, Creator and Growth, plan credits expire at the end of each billing month. On Pro, Agency and Enterprise, unused credits roll over for one month, capped at one month's allowance (so Pro can hold up to 800 and Agency up to 2,000). Top-up credits never expire." },
  { q: "What currency am I billed in?", a: "Australian customers are billed in Australian dollars and the prices shown include GST. New Zealand customers are billed in NZ dollars. Everyone else is billed in US dollars. Your billing country at checkout decides the price, and prices are fixed, not converted on the day." },
  { q: "Is annual billing available?", a: "Yes, Starter, Creator, Growth and Pro can be paid annually at 10 times the monthly price, so you get two months free. Your credits are still added every month. Agency is billed monthly only." },
  { q: "Does it work with my Shopify store?", a: "Yes. Paste any product URL from Shopify, Amazon or a custom storefront and Lumora imports the title, images, price and description into the ad generator. Works with Shopify AU, NZ and US storefronts." },
  { q: "Will the ads look fake or AI-generated?", a: "Lumora uses Grok Imagine for photo-real output and keeps the same creator consistent across a clip, so the result looks filmed on a phone rather than stitched together. Start on the free plan and judge for yourself." },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <AnimatedSection className="py-20 md:py-24 relative" id="faq">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-purple-300 mb-6">FAQ</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4"><span className="gradient-text">Frequently Asked Questions</span></h2>
          <p className="text-white/60">Everything you need to know about AI UGC ads and Lumora</p>
        </div>
        <div className="space-y-3" data-testid="faq-section" itemScope itemType="https://schema.org/FAQPage">
          {FAQ_ITEMS.map((item, i) => (
            <div key={item.q} className="glass rounded-2xl overflow-hidden" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full px-6 py-5 text-left flex items-center justify-between"
                data-testid={`faq-item-${i}`}
              >
                <span className="font-bold text-white text-sm pr-4" itemProp="name">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-white/40 transition-transform flex-shrink-0 ${openIndex === i ? "rotate-180" : ""}`} />
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-white/60 text-sm leading-relaxed" itemProp="text">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const BlogCTASection = () => (
  <AnimatedSection className="py-16 relative">
    <div className="max-w-5xl mx-auto px-6 md:px-12">
      <div className="glass rounded-3xl p-8 md:p-12 text-center" data-testid="blog-cta">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-4"><span className="gradient-text">Master AI Ad Strategy</span></h2>
        <p className="text-white/65 mb-6 max-w-xl mx-auto">Read tactical playbooks on launching AI UGC ads, optimizing ROAS, and scaling creator-style content for e-commerce.</p>
        <Link to="/blog" className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold hover:from-violet-500 hover:to-indigo-500 transition-all" data-testid="blog-cta-link">
          Start Reading <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </AnimatedSection>
);

export default function LandingPage() {
  useEffect(() => { document.title = "Lumora AI | AI-Powered UGC Ad Generator for E-Commerce & Fanvue"; }, []);
  return (
    <>
      <SEO
        title="AI-Powered UGC Ad Generator for E-Commerce & Fanvue"
        description="Finished 15-second UGC video ads from your product URL. Plans from A$22/month inc GST (US$13 elsewhere). Start free with 30 credits, no credit card required."
        path="/"
      />
      <Navbar />
      <HeroSection />
      <AdExamplesGallery />
      <HowItWorksSection />
      <LocalSocialProofStrip />
      <ServicesSection />
      <OutcomeValuePropsSection />
      <PricingSection />
      <FAQSection />
      <ContactSection />
      <BlogCTASection />
      <Footer />
      <StickyCTABar />
      <div className="noise-overlay" />
    </>
  );
}

